import { PDFDocument, PDFArray, PDFStream, PDFContentStream, type PDFPage, rgb, degrees } from "pdf-lib";
import { pageView } from "./pageView";
import { drawTextAnywhere } from "./drawTextAnywhere";
import { hexToRgb01 } from "./editorStyle";
import { parsePdfFormFields, fillPdfForm } from "./formFill";
import type { FieldFailure } from "./formAppearance";

/** One thing a person put on the page in the PDF Editor (positions are fractions of the page, top-left origin). */
export interface EditorAnnotation {
  id: number;
  page: number;
  xFrac: number;
  yFrac: number;
  type: "text" | "box" | "rectangle" | "ellipse" | "freehand";
  text?: string;
  widthFrac?: number;
  heightFrac?: number;
  points?: { x: number; y: number }[];
  color: string;
  strokeW: number;
  size: number;
  bold?: boolean;
  fill?: boolean;
  cover?: { x: number; y: number; w: number; h: number };
  lineKey?: string;
}

export interface ExportResult {
  bytes: Uint8Array;
  /** Items actually drawn into the file. */
  written: number;
  /** Items that could not be drawn, with a plain-language reason, so the person is told before download. */
  skipped: { id: number; type: EditorAnnotation["type"]; page: number; reason: string }[];
}

/** An item with nothing to draw (an empty text box) is not an error and not counted. */
export function isEmptyItem(a: EditorAnnotation): boolean {
  return a.type === "text" && !a.cover && !a.text?.trim();
}

/** Strokes need a positive width; a missing or zero value (older state, odd input) would draw nothing. */
function strokeWidth(a: EditorAnnotation): number {
  return Number.isFinite(a.strokeW) && a.strokeW > 0 ? a.strokeW : 2;
}

/** Total operators / bytes of a page's content streams, to prove a draw call really wrote something. */
function contentSize(pdf: PDFDocument, page: PDFPage): number {
  const c = page.node.Contents();
  const streams = c instanceof PDFArray ? c.asArray().map((r) => pdf.context.lookup(r)) : c ? [c] : [];
  return streams.reduce((n, st) => n + (st instanceof PDFContentStream ? (st as unknown as { operators: unknown[] }).operators.length : st instanceof PDFStream ? st.getContentsSize() : 0), 0);
}

/** Draws every item onto the PDF as page content and reports exactly what was and wasn't written. */
export async function exportAnnotations(source: ArrayBuffer | Uint8Array, annotations: EditorAnnotation[]): Promise<ExportResult> {
  const pdf = await PDFDocument.load(source);
  const pages = pdf.getPages();
  pdf.setProducer("FreeDailyPro");
  const skipped: ExportResult["skipped"] = [];
  let written = 0;

  for (const ann of annotations) {
    if (isEmptyItem(ann)) continue;
    const page = pages[ann.page - 1];
    if (!page) { skipped.push({ id: ann.id, type: ann.type, page: ann.page, reason: "its page is missing" }); continue; }
    // Everything is drawn as the reader sees the page (turned and cropped as shown), see pageView.ts.
    const view = pageView(page);
    const { width, height } = view;
    const rot = degrees(view.rotation);
    const at = (vx: number, vy: number) => view.toStored(vx, vy);
    const c = hexToRgb01(ann.color);
    const col = rgb(c.r, c.g, c.b);
    const bw = strokeWidth(ann);
    const before = contentSize(pdf, page);
    try {
      if (ann.type === "text") {
        if (ann.cover) {
          const cv = ann.cover;
          const p0 = at(cv.x * width, height - (cv.y + cv.h) * height);
          page.drawRectangle({ x: p0.x, y: p0.y, width: cv.w * width, height: cv.h * height, color: rgb(1, 1, 1), borderWidth: 0, rotate: rot });
        }
        if (ann.text?.trim()) {
          const x = ann.xFrac * width;
          const y = height - ann.yFrac * height;
          const p0 = at(x, y - ann.size * 0.86);
          await drawTextAnywhere(pdf, page, ann.text.replace(/[\r\n\t]+/g, " ").trim(), { x: p0.x, y: p0.y, size: ann.size, bold: ann.bold, color: c, rotateDegrees: view.rotation });
        }
      } else if (ann.type === "box" || ann.type === "rectangle") {
        const boxWidth = (ann.widthFrac || 0.15) * width;
        const boxHeight = (ann.heightFrac || 0.05) * height;
        const p0 = at(ann.xFrac * width, height - ann.yFrac * height - boxHeight);
        if (ann.type === "box") {
          page.drawRectangle({ x: p0.x, y: p0.y, width: boxWidth, height: boxHeight, color: col, opacity: 0.4, rotate: rot });
        } else {
          page.drawRectangle({ x: p0.x, y: p0.y, width: boxWidth, height: boxHeight, borderColor: col, borderWidth: bw, borderOpacity: 1, ...(ann.fill ? { color: col, opacity: 0.2 } : {}), rotate: rot });
        }
      } else if (ann.type === "ellipse") {
        const boxWidth = (ann.widthFrac || 0.15) * width;
        const boxHeight = (ann.heightFrac || 0.05) * height;
        const ec = at(ann.xFrac * width + boxWidth / 2, height - (ann.yFrac * height + boxHeight / 2));
        page.drawEllipse({ x: ec.x, y: ec.y, xScale: boxWidth / 2, yScale: boxHeight / 2, borderColor: col, borderWidth: bw, borderOpacity: 1, ...(ann.fill ? { color: col, opacity: 0.2 } : {}), rotate: rot });
      } else if (ann.type === "freehand") {
        if (!ann.points || ann.points.length < 2) throw new Error("the stroke has fewer than two points");
        const svgPoints = ann.points.map((p) => `${(p.x * width).toFixed(1)},${(p.y * height).toFixed(1)}`);
        const svgPath = `M ${svgPoints[0]} L ${svgPoints.slice(1).join(" L ")}`;
        const o = at(0, height);
        page.drawSvgPath(svgPath, { x: o.x, y: o.y, borderColor: col, borderWidth: bw, rotate: rot });
      }
      // Never trust a draw call that returned quietly: if the page's content did not grow, nothing was written.
      if (contentSize(pdf, page) <= before) throw new Error("nothing was drawn on the page");
      written++;
    } catch (err) {
      console.warn("Could not write one item:", { annotation: ann, error: err });
      skipped.push({ id: ann.id, type: ann.type, page: ann.page, reason: err instanceof Error ? err.message : "unknown error" });
    }
  }
  return { bytes: await pdf.save(), written, skipped };
}

/** What the person typed into the PDF's own form fields (by field name). */
export interface FormAnswers { values: Record<string, string>; checked: Record<string, boolean> }

export interface EditedPdf extends ExportResult {
  /** Form fields that could not be filled, with the reason. */
  formFailed: FieldFailure[];
}

/** Fills the PDF's own form fields (when answers are given), then draws every item on top. Nothing is dropped without being reported. */
export async function exportEditedPdf(file: File, annotations: EditorAnnotation[], answers?: FormAnswers): Promise<EditedPdf> {
  let source: Uint8Array = new Uint8Array(await file.arrayBuffer());
  let formFailed: FieldFailure[] = [];
  if (answers) {
    const form = await parsePdfFormFields(file);
    if (form.fields.length > 0 && !form.hasXfa) {
      const filled = await fillPdfForm(form.pdfDoc, form.fields, answers.values, answers.checked, false, form.initialLists);
      formFailed = filled.failed;
      source = new Uint8Array(await filled.blob.arrayBuffer());
    }
  }
  const out = await exportAnnotations(source, annotations);
  return { ...out, formFailed };
}
